// services/api.js
import { getConfig } from '../config.js';
import { 
  answerTextMappings,
  calculateRecommendation,
  formatRecommendation
} from '../stores/surveyStore.js';

// Get environment configuration
const config = getConfig();

/**
 * Generate a DynamoDB-formatted timestamp string.
 * Exported so SurveyContainer can call it once at session start.
 */
export function generateTimestamp() {
  const now = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
         `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.` +
         `${pad(now.getMilliseconds(), 6)}`;
}

/**
 * Format survey data for API submission with exact DynamoDB structure
 * @param {Object} surveyData - Survey responses
 * @param {string} campaign - Campaign name
 * @param {Object} recommendationInfo - Optional recommendation data
 * @param {Object} dropOffInfo - Optional drop-off tracking data
 * @returns {Object} Formatted data for DynamoDB
 */
export function formatSurveyForAPI(surveyData, campaign = "Charge Nation Recommendations", recommendationInfo = null, dropOffInfo = null) {
  console.log('Raw survey data before formatting:', surveyData);
  
  // Use the session sk if available (set once at survey start), else fall back to now
  const sk = surveyData.sk || generateTimestamp();
  // lastUpdatedAt always reflects the actual time of this save
  const lastUpdatedAt = generateTimestamp();
  
  // Start with the required DynamoDB fields
  const apiData = {
    "campaign": campaign
  };

  // Always write email if present
  if (surveyData.email) {
    apiData.email = surveyData.email;
  }

  // isAnonymous: respect whatever the caller explicitly passes.
  // - saveIntermediate always passes isAnonymous: true (even if email is known)
  // - submitSurvey passes the raw store, so isAnonymous is not set → false when email exists
  apiData.isAnonymous = surveyData.isAnonymous === true
    ? true
    : !!surveyData.email === false; // no email on final submit → still anonymous
  
  if (surveyData.source) {
    apiData.source = surveyData.source;
  }

  apiData.sk = sk;
  apiData.lastUpdatedAt = lastUpdatedAt;

  if (dropOffInfo) {
    apiData.dropOffQues = dropOffInfo.question;
    apiData.numOfQues = dropOffInfo.numOfQues;
  }
  
  // Question 1: Inspiration - check all possible keys
  const inspirationId = surveyData.inspiration || surveyData.q1 || surveyData['multi-choice-1'] || '';
  if (inspirationId) {
    apiData["What most inspires you to explore Charge Nation today?"] = 
      answerTextMappings.inspiration[inspirationId] || inspirationId;
  }

  // Capture utm source
  if (surveyData.source) {
    apiData.source = surveyData.source;
  }
  
  // Question 2: Games wanted - check all possible keys
  const attendanceId = surveyData.attendance || surveyData.q2 || surveyData['multi-choice-2'] || '';
  if (attendanceId) {
    apiData["How many Charge games do you typically attend per season?"] = 
      answerTextMappings.attendance[attendanceId] || attendanceId;
  }
  
  // Question 3: Seating - check all possible keys including image-multi-choice
  const seatingId = surveyData.seating || surveyData.q3 || surveyData['seating-3'] || 
                   surveyData['image-multi-choice-3'] || '';
  if (seatingId) {
    apiData["When you come out to Charge games where do you typically like to sit?"] = 
      answerTextMappings.seating[seatingId] || seatingId;
  }
  
  // Question 4: Companions (multi-select) - check all possible keys
  const companionsData = surveyData.companions || surveyData.q4 || surveyData['multi-choice-4'] || 
                        surveyData['multi-select-4'];
  if (companionsData) {
    let selectedCompanions = [];
    
    // Handle different possible formats
    if (Array.isArray(companionsData)) {
      // Array format - convert directly
      selectedCompanions = companionsData.map(id => answerTextMappings.companions[id] || id);
    } else if (typeof companionsData === 'object' && !Array.isArray(companionsData)) {
      // Object format with boolean values - convert to array of selected IDs
      const selectedIds = Object.entries(companionsData)
        .filter(([_, selected]) => selected === true || selected === 1 || selected === '1')
        .map(([id]) => id);
      selectedCompanions = selectedIds.map(id => answerTextMappings.companions[id] || id);
    } else if (typeof companionsData === 'string') {
      // Single selection as string - convert to array with one item
      selectedCompanions = [answerTextMappings.companions[companionsData] || companionsData];
    }
    
    apiData["Who do you typically attend Charge games with?"] = selectedCompanions;
  }

  // Question 6: Ranking question
  const rankingData = surveyData.ranking || surveyData.q6 || surveyData['ranking-6'];
  const answered = surveyData.ranking_answered || surveyData.q6_answered || surveyData['ranking-6_answered'];

  if (!answered) {
    apiData["Rank your motivations"] = null; // user skipped
  } else if (rankingData) {
    let rankingArray = [];

    if (Array.isArray(rankingData)) {
      rankingArray = rankingData;
    } else if (typeof rankingData === 'object') {
      rankingArray = Object.entries(rankingData)
        .sort(([, rankA], [, rankB]) => rankA - rankB)
        .map(([id]) => id);
    }

    const rankingMapped = rankingArray.map(
      id => answerTextMappings.motivations?.[id] || id
    );

    apiData["Rank your motivations"] = rankingMapped;
  }


  
  // Question 7: Playoff Importance - numbered differently in different places
  const playoffImportanceId = surveyData.playoffImportance || surveyData.q7 ||
                            surveyData['multi-choice-7'] || '';
  if (playoffImportanceId) {
    apiData["How important is playoff ticket access to you?"] = 
      answerTextMappings.playoffImportance[playoffImportanceId] || playoffImportanceId;
  }
  
  // Question 9: Perks (Multi-select) - numbered differently in different places
  const perksData = surveyData.perks || surveyData.q9 || 
                  surveyData['multi-choice-9'] || surveyData['multi-select-9'];
  if (perksData) {
    let selectedPerks = [];
    
    // Handle different possible formats
    if (Array.isArray(perksData)) {
      // Array format - convert directly
      selectedPerks = perksData.map(id => answerTextMappings.perks[id] || id);
    } else if (typeof perksData === 'object' && !Array.isArray(perksData)) {
      // Object format with boolean values - convert to array of selected IDs
      const selectedIds = Object.entries(perksData)
        .filter(([_, selected]) => selected === true || selected === 1 || selected === '1')
        .map(([id]) => id);
      selectedPerks = selectedIds.map(id => answerTextMappings.perks[id] || id);
    } else if (typeof perksData === 'string') {
      // Single selection as string - convert to array with one item
      selectedPerks = [answerTextMappings.perks[perksData] || perksData];
    }
    
    apiData["Which of these other perks are important to you?"] = selectedPerks;
  }

  // Question 10: Promotions (Multi-select) - numbered differently in different places
  const promotionsData = surveyData.promotions || surveyData.q10 || 
                  surveyData['multi-choice-10'] || surveyData['multi-select-10'];
  if (promotionsData) {
    let selectedPromotions = [];
    
    // Handle different possible formats
    if (Array.isArray(promotionsData)) {
      // Array format - convert directly
      selectedPromotions = promotionsData.map(id => answerTextMappings.perks[id] || id);
    } else if (typeof promotionsData === 'object' && !Array.isArray(promotionsData)) {
      // Object format with boolean values - convert to array of selected IDs
      const selectedIds = Object.entries(promotionsData)
        .filter(([_, selected]) => selected === true || selected === 1 || selected === '1')
        .map(([id]) => id);
      selectedPromotions = selectedIds.map(id => answerTextMappings.perks[id] || id);
    } else if (typeof promotionsData === 'string') {
      // Single selection as string - convert to array with one item
      selectedPromotions= [answerTextMappings.promotions[promotionsData] || promotionsData];
    }
    
    apiData["Which promotions are the most important to you?"] = selectedPromotions;
  }

  // const seatCountId = surveyData.seatCount || surveyData.q9 || surveyData['multi-choice-9'] || '';
  // if (seatCountId) {
  //   apiData["How many seats are you interested in?"] = 
  //     answerTextMappings.seatCount[seatCountId] || seatCountId;
  // }
  
  // Add recommendation info if provided
  if (recommendationInfo) {
    apiData["Recommended Package"] = recommendationInfo.packageName;
    apiData["Package Details"] = {
      games: recommendationInfo.details.games,
      pricing: recommendationInfo.details.pricing || '',
      playoffAccess: recommendationInfo.details.playoffAccess,
      benefits: recommendationInfo.details.benefits,
      addedValue: recommendationInfo.details.addedValue
    };
    
    if (recommendationInfo.packageLink) {
      apiData["Package Link"] = recommendationInfo.packageLink;
    }
  }
  
  return apiData;
}

/**
 * Get the appropriate package link based on recommendation
 * @param {string} packageType - Quarter, Half, Full
 * @returns {string} Link to the package
 */
export function getPackageLink(packageType) {
  const packageLinks = {
    "Half": "https://seatgeek.com/cleveland-charge-tickets/ticket-packages",
    "Full": "https://seatgeek.com/cleveland/series/35941001-0014-eb72-a971-08de3c04070a?performer_id=265663"
  };
  
  return packageLinks[packageType] || packageLinks["Half"]; // Default to Quarter if not found
}

/**
 * Fetch survey questions from the API
 * @returns {Promise<Array>} Transformed questions ready for frontend use
 */
export async function fetchQuestions() {
  try {
    console.log(`Fetching questions from ${config.apiEndpoint}...`);
    const response = await fetch(`${config.apiEndpoint}/questions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      },
      body: JSON.stringify({
        campaign: 'Charge Nation Recommendations'
      })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return transformQuestionsForFrontend(data);
  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error; // No fallback - propagate the error
  }
}

/**
 * Save intermediate survey progress (per-step / drop-off capture).
 * Always marks isAnonymous: true — final submitSurvey will flip it if email exists.
 * Fire-and-forget; errors are swallowed so they never block navigation.
 * @param {Object} surveyData - Current survey store state
 * @param {Object} currentQuestion - The question currently displayed
 * @param {number} stepIndex - The current step index
 */
export async function saveIntermediate(surveyData, currentQuestion, stepIndex) {
  try {
    const formattedData = formatSurveyForAPI(
      { ...surveyData, isAnonymous: true },
      "Charge Nation Recommendations",
      null,
      {
        question: currentQuestion?.title || `Step ${stepIndex}`,
        numOfQues: stepIndex
      }
    );

    await fetch(`${config.apiEndpoint}/responses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      },
      body: JSON.stringify(formattedData),
    });
  } catch (err) {
    console.warn('Intermediate save failed (non-blocking):', err);
  }
}

/**
 * Submit the completed survey
 * @param {Object} surveyData - Survey responses from user
 * @returns {Promise<Object>} Submission response
 */
export async function submitSurvey(surveyData) {
  try {
    console.log('Starting survey submission process');
    
    // Calculate recommendation for the user
    const recommendationCode = calculateRecommendation(surveyData);
    const formattedRecommendation = formatRecommendation(recommendationCode);
    
    // Get the package type (Quarter, Half, Full, Premium)
    const packageType = recommendationCode.split(' / ')[0];
    
    // Add the package link to the recommendation
    formattedRecommendation.packageLink = getPackageLink(packageType);
    
    // Format the data for DynamoDB structure with recommendation included
    const formattedData = formatSurveyForAPI(
      surveyData, 
      "Charge Nation Recommendations", 
      formattedRecommendation
    );
    
    console.log('Sending data to API with recommendation:', formattedData);
    
    const response = await fetch(`${config.apiEndpoint}/responses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      },
      body: JSON.stringify(formattedData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error (${response.status}):`, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }
    
    const responseData = await response.json();
    console.log('API submission successful:', responseData);
    

    return responseData;
  } catch (error) {
    console.error('Error submitting survey:', error);
    throw error;
  }
}


/**
 * Retrieve a recommendation based on survey answers
 * @param {Object} surveyData - Survey responses
 * @returns {Promise<Object>} Recommendation data
 */
export async function getRecommendation(surveyData) {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        const recommendationCode = calculateRecommendation(surveyData);
        const formattedRecommendation = formatRecommendation(recommendationCode);
        
        // Add the package link to the recommendation
        const packageType = recommendationCode.split(' / ')[0];
        formattedRecommendation.packageLink = getPackageLink(packageType);
        
        resolve({
          success: true,
          data: formattedRecommendation
        });
      }, 1000);
    });
  } catch (error) {
    console.error('Error getting recommendation:', error);
    throw error;
  }
}

/**
 * Transform API question format to frontend format
 * @param {Array} apiQuestions - Questions from API
 * @returns {Array} Questions in frontend format
 */
function transformQuestionsForFrontend(apiQuestions) {
  // Map type from API format to frontend format
  const typeMap = {
    'multiple choice': 'multi-choice',
    'team-selection': 'team-selection',
    'ranking': 'ranking',
    'email': 'email',
    'informational': 'info'
  };
  
  // Sort questions by sk (sort key)
  const sortedQuestions = [...apiQuestions].sort((a, b) => parseInt(a.sk) - parseInt(b.sk));
  
  // Add intro question
  const transformedQuestions = [{
    id: 'intro',
    type: 'intro',
    title: 'Find Your Perfect Charge Nation Membership',
    mainParagraph: 'A Charge Nation Membership offers season-long access to Charge games through half and full season package options. Beyond game attendance, members enjoy a range of year-round benefits, including Team Shop discounts, exclusive experiences, and special perks that increase with membership tenure.',
    description: 'Answer a few questions to discover the membership package that best fits your preferences.'
  }];
  
  // Transform each question
  sortedQuestions.forEach(apiQuestion => {
    const baseQuestionData = {
      id: `q${apiQuestion.sk}`,
      title: apiQuestion.question,
      description: apiQuestion.instructions || ''
    };

    switch (apiQuestion.type) {
      case 'informational':
        transformedQuestions.push({
          ...baseQuestionData,
          id: `info-${apiQuestion.sk}`,
          type: 'info',
          video_link: apiQuestion.video_link
        });
        break;

      case 'email':
        transformedQuestions.push({
          ...baseQuestionData,
          id: 'email',
          type: 'email',
          description: 'We\'ll email your personalized membership recommendation.',
          validationRules: apiQuestion.validationRules || {}
        });
        break;

      case 'ranking':
        // Ranking question: convert answers array to options and default ranks
        transformedQuestions.push({
          ...baseQuestionData,
          type: 'ranking',
          options: apiQuestion.answers.map(answer => ({
            id: answer.id || `opt-${answer.order}`,
            label: answer.text
          })),
          // Optional: store initial ranking (1,2,3...) for frontend initialization
          initialRanks: apiQuestion.answers.reduce((acc, answer, idx) => {
            acc[answer.id || `opt-${answer.order}`] = idx + 1;
            return acc;
          }, {})
        });
        break;

      case 'image multiple choice':
        transformedQuestions.push({
          ...baseQuestionData,
          type: 'multi-choice',
          options: apiQuestion.answers.map(answer => ({
            id: answer.id || answer.text.toLowerCase().replace(/\s+/g, '-'),
            label: answer.text,
            image: answer.imageUrl
          }))
        });
        break;

      default:
        // Handle multi-choice / multi-select
        const questionType = apiQuestion.allowMultipleSelections ? 'multi-select' : 'multi-choice';
        transformedQuestions.push({
          ...baseQuestionData,
          id: apiQuestion.id || `${questionType}-${apiQuestion.sk}`,
          type: questionType,
          options: apiQuestion.answers.map(answer => ({
            id: answer.id || `opt-${answer.order}`,
            label: answer.text,
            ...(answer.imageUrl && { image: answer.imageUrl })
          }))
        });
        break;
    }
  });

  // Ensure email question is in position 1
  const emailIndex = transformedQuestions.findIndex(q => q.id === 'email');
  if (emailIndex > 1) {
    const emailQuestion = transformedQuestions.splice(emailIndex, 1)[0];
    transformedQuestions.splice(1, 0, emailQuestion);
  }

  return transformedQuestions;
}

